import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import toastr from 'toastr';

// Import API configuration and headers from single source of truth
import { API_URL, BASE_URL, PROFILE_IMG_PATH, LOGO_IMG_PATH, getAuthHeaders, getMultipartHeaders } from '../../actions/utilAction';

// Re-export for components that import from adminSlice
export { API_URL, BASE_URL, PROFILE_IMG_PATH, LOGO_IMG_PATH };

// TypeScript interfaces for data models
interface Sport {
  _id: string;
  sportname: string;
  name?: string;
  status?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Team {
  _id: string;
  teamname: string;
  name?: string;
  logo?: string;
  sport?: Sport | string;
  status?: boolean;
}

interface TeamDetails {
  _id: string;
  teamname: string;
  logo?: string;
  sport?: Sport;
  status?: boolean;
}

interface DashboardCounts {
  userCount?: number;
  sportCount?: number;
  teamCount?: number;
  gameCount?: number;
  compCount?: number;
}

interface GraphStats {
  month: string;
  count: number;
}

interface Bonus {
  _id: string;
  bonusname: string;
  description: string;
  name?: string;
  value?: number;
  sport?: Sport | string;
}

interface Round {
  _id: string;
  roundno: number;
  roundtype?: string;
  sport?: Sport | string;
  season?: string;
  status?: boolean;
}

interface Game {
  _id: string;
  homeTeam: Team | string;
  awayTeam: Team | string;
  round?: Round | string;
  sport?: Sport | string;
  gameDate?: string;
  gameTime?: string;
  status?: boolean;
  isStarted?: boolean;
  isResultDeclared?: boolean;
}

interface Topic {
  _id: string;
  topicname: string;
  status?: boolean;
}

interface Question {
  _id: string;
  question: string;
  answer: string;
  topic?: Topic | string;
}

interface Content {
  _id: string;
  title: string;
  content: string;
  type?: string;
}

interface User {
  _id: string;
  name: string;
  email: string;
  role?: { rolename: string };
  status?: boolean;
}

interface Competition {
  _id: string;
  competitionname: string;
  compname?: string;
  sport?: Sport | string;
  totalCount?: number;
  status?: boolean;
}

interface Settings {
  _id?: string;
  isMaintenanceMode?: boolean;
  isTopSportEnabled?: boolean;
}

interface AdReport {
  _id: string;
  adId: string;
  impressions: number;
  clicks: number;
  date?: string;
}

interface Ad {
  _id: string;
  name: string;
  type: string;
  mediaType: string;
  userType: string;
  sport: string;
  pages?: string;
  redirectUrl?: string;
  image?: string;
  status?: boolean;
}

interface Season {
  _id: string;
  seasonname: string;
  sport?: Sport | string;
  startDate?: string;
  endDate?: string;
  status?: boolean;
}

interface SeasonDetails {
  _id: string;
  seasonname: string;
  sport?: Sport;
  teams?: Team[];
}

interface Version {
  _id: string;
  version: string;
  platform?: string;
  forceUpdate?: boolean;
}

interface LadderEntry {
  _id: string;
  user: User | string;
  comp?: Competition | string;
  points: number;
  rank?: number;
}

interface SecretCompGame {
  _id: string;
  game?: Game | string;
  comp?: Competition | string;
}

interface Article {
  _id: string;
  title?: string;
  description?: string;
  content?: string;
  image?: string;
  isActive: boolean;
  isDeleted?: boolean;
  status?: boolean;
  createdAt?: string;
}

// Parameter interfaces for async thunks
interface ListParams {
  page?: number;
  sortValue?: string;
  sortOrder?: number | string;
  count?: number;
  limit?: number;
  isActive?: boolean;
}

interface SportParams extends ListParams {
  sportId?: string;
  sportname?: string;
  name?: string;
}

interface TeamParams extends ListParams {
  teamId?: string;
  teamname?: string;
  sport?: string;
}

interface BonusParams extends ListParams {
  bonusId?: string;
  bonusname?: string;
  description?: string;
}

interface RoundParams extends ListParams {
  roundId?: string;
  roundno?: number;
  sportId?: string;
  season?: string;
}

interface GameParams extends ListParams {
  gameId?: string;
  homeTeam?: string;
  awayTeam?: string;
  round?: string;
  sport?: string;
  sportId?: string;
  gameDate?: string | Date;
  gameTime?: string | Date;
  date?: Date;
  time?: Date;
  selectedSeason?: string;
  season?: string;
  teamId?: string;
  homeTeamPoints?: number;
  awayTeamPoints?: number;
  eventId?: string;
  winningTeam?: string;
  drawPoints?: string | number;
  gameState?: string;
  kingbotTipping?: string;
}

interface TopicParams extends ListParams {
  topicId?: string;
  topicname?: string;
}

interface QuestionParams extends ListParams {
  questionId?: string;
  question?: string;
  answer?: string;
  topicId?: string;
}

interface ContentParams extends ListParams {
  contentId?: string;
  title?: string;
  content?: string;
}

interface CompParams extends ListParams {
  compId?: string;
  competitionId?: string;
}

interface SeasonParams extends ListParams {
  seasonId?: string;
  seasonname?: string;
  sportId?: string;
}

interface VersionParams extends ListParams {
  versionId?: string;
  version?: string;
  platform?: string;
  forceUpdate?: boolean;
}

interface AdParams extends ListParams {
  adId?: string;
  name?: string;
  type?: string;
  filter?: string;
}

interface LadderParams extends ListParams {
  compId?: string;
  roundId?: string;
  sportId?: string;
}

interface ArticleParams extends ListParams {
  articleId?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  search?: string;
}

interface AdminState {
  // Sport
  sportData: Sport[];
  sportCount: number;
  // Dashboard
  dashboardCounts: DashboardCounts | null;
  graphStats: GraphStats[];
  // Team
  teamData: Team[];
  teamCount: number;
  teamDetails: TeamDetails | null;
  // Bonus
  bonusData: Bonus[];
  bonusCount: number;
  // Round
  roundData: Round[];
  roundCount: number;
  // Game
  gameData: Game[];
  gameCount: number;
  // Topic
  topicData: Topic[];
  topicCount: number;
  // Question
  questionData: Question[];
  questionCount: number;
  // Content
  contentData: Content[];
  contentCount: number;
  // User
  userData: User[];
  userCount: number;
  // Competition
  competitionData: Competition[];
  competitionCount: number;
  // Settings
  settingData: Settings | null;
  // Ad Reports
  adreportData: AdReport[];
  adreportCount: number;
  // Ad
  adData: Ad[];
  adCount: number;
  // Comp
  compData: Competition[];
  compCount: number;
  // Season
  seasonData: Season[];
  seasonCount: number;
  seasonDetails: SeasonDetails | null;
  // Version
  versionData: Version[];
  versionCount: number;
  // Ladder
  ladderData: LadderEntry[];
  ladderDataCount: number;
  // Secret Comp Game
  secretCompGameDate: SecretCompGame[];
  // Article
  articleData: Article[];
  articleCount: number;
  articleLimit: number;
  // Loading states
  loading: boolean;
  error: string | null;
}

const initialState: AdminState = {
  sportData: [],
  sportCount: 0,
  dashboardCounts: null,
  graphStats: [],
  teamData: [],
  teamCount: 0,
  teamDetails: null,
  bonusData: [],
  bonusCount: 0,
  roundData: [],
  roundCount: 0,
  gameData: [],
  gameCount: 0,
  topicData: [],
  topicCount: 0,
  questionData: [],
  questionCount: 0,
  contentData: [],
  contentCount: 0,
  userData: [],
  userCount: 0,
  competitionData: [],
  competitionCount: 0,
  settingData: null,
  adreportData: [],
  adreportCount: 0,
  adData: [],
  adCount: 0,
  compData: [],
  compCount: 0,
  seasonData: [],
  seasonCount: 0,
  seasonDetails: null,
  versionData: [],
  versionCount: 0,
  ladderData: [],
  ladderDataCount: 0,
  secretCompGameDate: [],
  articleData: [],
  articleCount: 0,
  articleLimit: 10,
  loading: false,
  error: null,
};

// API Endpoints
const ENDPOINTS = {
  SPORT_LIST: 'sport/listSport',
  ADD_SPORT: 'sport/addupdateSport',
  REMOVE_SPORT: 'sport/deleteSport',
  TEAM_LIST: 'team/getTeamList',
  ADD_TEAM: 'team/addTeam',
  REMOVE_TEAM: 'team/deleteTeam',
  TEAM_DETAIL: 'team/getTeamDetail',
  TEAM_UPDATE: 'team/updateTeam',
  UPDATE_LOGO: 'team/updateTeamLogo',
  BLOCK_TEAM: 'team/blockTeam',
  BONUS_LIST: 'bonus/listBonus',
  ADD_BONUS: 'bonus/addBonus',
  REMOVE_BONUS: 'bonus/deleteBonus',
  ROUND_LIST: 'round/listRounds',
  ADD_ROUND: 'round/addRound',
  REMOVE_ROUND: 'round/deleteRound',
  BLOCK_ROUND: 'round/blockRound',
  UPDATE_ROUND: 'round/updateRound',
  GAME_LIST: 'game/listGames',
  ADD_GAME: 'game/addGames',
  REMOVE_GAME: 'game/deleteGame',
  UPDATE_GAME: 'game/updateGame',
  TOPIC_LIST: 'topic/listTopic',
  ADD_TOPIC: 'topic/addupdateTopic',
  REMOVE_TOPIC: 'topic/deleteTopic',
  QUESTION_LIST: 'question/listQuestion',
  ADD_QUESTION: 'question/addupdateQuestion',
  REMOVE_QUESTION: 'question/deleteQuestion',
  CONTENT_LIST: 'content/listContent',
  ADD_CONTENT: 'content/addupdateContent',
  USER_LIST: 'user/listUser',
  COMPETITION_LIST: 'competition/listCompetition',
  GET_SETTING: 'setting/getSetting',
  ADD_SETTING: 'setting/addupdateSetting',
  AD_LIST: 'ad/listAd',
  ADD_AD: 'ad/addUpdateAd',
  REMOVE_AD: 'ad/deleteAd',
  AD_REPORTS: 'ad/adReports',
  COMP_LIST: 'competition/listCompetition',
  REMOVE_COMP: 'competition/deleteCompetition',
  SEASON_LIST: 'season/getSeasonList',
  ADD_SEASON: 'season/addSeason',
  REMOVE_SEASON: 'season/deleteSeason',
  BLOCK_SEASON: 'season/blockSeason',
  SEASON_DETAIL: 'season/getTeamDetail',
  VERSION_LIST: 'version/versionList',
  ADD_VERSION: 'version/addversion',
  REMOVE_VERSION: 'version/deleteversion',
  ADMIN_COUNT_LIST: 'user/getAdminDashboardCounts',
  ADMIN_GRAPH_STATS: 'user/graphByMonth',
  USER_LADDER: 'ladder/filterLadderList',
  AUTO_TIPPING: 'tipping/addAutoTipping',
  SEND_GAME_START_NOTIFICATION: 'kingbot/sendGameStartNotification',
  UPDATE_GAME_SEASON: 'game/updateGameSeason',
  ARTICLE_LIST: 'article/getAndSearchArticle',
  ARTICLE_ACTIVE_DELETE: 'article/active/delete',
};

// ==================== SPORT ACTIONS ====================
export const getSport = createAsyncThunk(
  'admin/getSport',
  async (params: ListParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.SPORT_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { sportData: response.data.data, sportCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addSport = createAsyncThunk(
  'admin/addSport',
  async (authObj: SportParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_SPORT, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getSport({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeSport = createAsyncThunk(
  'admin/removeSport',
  async (obj: { sportId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_SPORT, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getSport({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== TEAM ACTIONS ====================
export const getTeam = createAsyncThunk(
  'admin/getTeam',
  async (params: TeamParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.TEAM_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { teamData: response.data.data, teamCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const getTeamDetails = createAsyncThunk(
  'admin/getTeamDetails',
  async (obj: { teamId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.TEAM_DETAIL, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { teamDetails: response.data.data };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addTeam = createAsyncThunk(
  'admin/addTeam',
  async (formData: FormData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_TEAM, formData, {
        headers: getMultipartHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getTeam({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeTeam = createAsyncThunk(
  'admin/removeTeam',
  async (obj: { teamId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_TEAM, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getTeam({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const updateTeam = createAsyncThunk(
  'admin/updateTeam',
  async (obj: { teamId: string; teamname?: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.TEAM_UPDATE, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getTeamDetails({ teamId: obj.teamId }));
        return response.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const blockTeam = createAsyncThunk(
  'admin/blockTeam',
  async (values: TeamParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.BLOCK_TEAM, values, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getTeam(values));
        return response.data;
      } else {
        toastr.error(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== BONUS ACTIONS ====================
export const getBonus = createAsyncThunk(
  'admin/getBonus',
  async (params: ListParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.BONUS_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { bonusData: response.data.data, bonusCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addBonus = createAsyncThunk(
  'admin/addBonus',
  async (authObj: BonusParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_BONUS, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getBonus({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeBonus = createAsyncThunk(
  'admin/removeBonus',
  async (obj: { bonusId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_BONUS, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getBonus({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== ROUND ACTIONS ====================
export const getRound = createAsyncThunk(
  'admin/getRound',
  async (params: RoundParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ROUND_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { roundData: response.data.data, roundCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addRound = createAsyncThunk(
  'admin/addRound',
  async (authObj: RoundParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_ROUND, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getRound({ page: 0 }));
        return response.data;
      } else if ([201, 400, 409].includes(response.data.code)) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const blockRound = createAsyncThunk(
  'admin/blockRound',
  async (values: RoundParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.BLOCK_ROUND, values, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getRound(values));
        return response.data;
      } else {
        toastr.error(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeRound = createAsyncThunk(
  'admin/removeRound',
  async (obj: { roundId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_ROUND, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getRound({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== GAME ACTIONS ====================
export const getGame = createAsyncThunk(
  'admin/getGame',
  async (params: GameParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.GAME_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { gameData: response.data.data, gameCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addGame = createAsyncThunk(
  'admin/addGame',
  async (authObj: GameParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_GAME, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getGame({ page: 0, season: authObj.selectedSeason }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeGame = createAsyncThunk(
  'admin/removeGame',
  async (obj: GameParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_GAME, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getGame({ page: 0, season: obj.selectedSeason }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const updateGame = createAsyncThunk(
  'admin/updateGame',
  async (obj: GameParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.UPDATE_GAME, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getGame({ teamId: obj.teamId, season: obj.selectedSeason }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== TOPIC ACTIONS ====================
export const getTopic = createAsyncThunk(
  'admin/getTopic',
  async (params: ListParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.TOPIC_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { topicData: response.data.data, topicCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addTopic = createAsyncThunk(
  'admin/addTopic',
  async (authObj: TopicParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_TOPIC, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getTopic({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeTopic = createAsyncThunk(
  'admin/removeTopic',
  async (obj: { topicId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_TOPIC, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getTopic({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== QUESTION ACTIONS ====================
export const getQuestion = createAsyncThunk(
  'admin/getQuestion',
  async (params: ListParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.QUESTION_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { questionData: response.data.data, questionCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addQuestion = createAsyncThunk(
  'admin/addQuestion',
  async (authObj: QuestionParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_QUESTION, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getQuestion({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeQuestion = createAsyncThunk(
  'admin/removeQuestion',
  async (obj: { questionId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_QUESTION, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getQuestion({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== CONTENT ACTIONS ====================
export const getContent = createAsyncThunk(
  'admin/getContent',
  async (params: ListParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.CONTENT_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { contentData: response.data.data, contentCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addContent = createAsyncThunk(
  'admin/addContent',
  async (authObj: ContentParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_CONTENT, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getContent({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== USER ACTIONS ====================
export const getUsers = createAsyncThunk(
  'admin/getUsers',
  async (params: ListParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.USER_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { userData: response.data.data, userCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== COMPETITION ACTIONS ====================
export const getCompetitions = createAsyncThunk(
  'admin/getCompetitions',
  async (params: CompParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.COMPETITION_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { competitionData: response.data.data, competitionCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== COMP ACTIONS ====================
export const getComp = createAsyncThunk(
  'admin/getComp',
  async (params: CompParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.COMP_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { compData: response.data.data, compCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeComp = createAsyncThunk(
  'admin/removeComp',
  async (obj: { compId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_COMP, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getComp({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const resetCompUserGamePoints = createAsyncThunk(
  'admin/resetCompUserGamePoints',
  async (obj: { params: { compId: string; sportId: string } }, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_URL + 'ladder/resetCompUserGamePoints', {
        params: obj.params,
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const resetAllCompUserGamePoints = createAsyncThunk(
  'admin/resetAllCompUserGamePoints',
  async (obj: { params: { sportId?: string } }, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_URL + 'ladder/resetAllCompUserGamePoints', {
        params: obj.params,
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== SETTINGS ACTIONS ====================
export const getSetting = createAsyncThunk(
  'admin/getSetting',
  async (params: { type?: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.GET_SETTING, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { settingData: response.data.data };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addSetting = createAsyncThunk(
  'admin/addSetting',
  async (authObj: { settingId?: string; isMaintenanceMode?: boolean; isTopSportEnabled?: boolean; currentDate?: string; isTesting?: boolean }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_SETTING, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getSetting({ type: 'testing' }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== AD ACTIONS ====================
export const getAd = createAsyncThunk(
  'admin/getAd',
  async (params: AdParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.AD_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { adData: response.data.data, adCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addAd = createAsyncThunk(
  'admin/addAd',
  async (formData: FormData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_AD, formData, {
        headers: getMultipartHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getAd({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeAd = createAsyncThunk(
  'admin/removeAd',
  async (obj: { adId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_AD, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getAd({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const getAdReports = createAsyncThunk(
  'admin/getAdReports',
  async (params: AdParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.AD_REPORTS, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { adreportData: response.data.data, adreportCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== SEASON ACTIONS ====================
export const getSeason = createAsyncThunk(
  'admin/getSeason',
  async (params: SeasonParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.SEASON_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { seasonData: response.data.data, seasonCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addSeason = createAsyncThunk(
  'admin/addSeason',
  async (authObj: SeasonParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_SEASON, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getSeason({ page: 0 }));
        return response.data;
      } else if ([201, 400, 409].includes(response.data.code)) {
        toastr.error(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeSeason = createAsyncThunk(
  'admin/removeSeason',
  async (obj: { seasonId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_SEASON, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.warning(response.data.message);
        dispatch(getSeason({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const blockSeason = createAsyncThunk(
  'admin/blockSeason',
  async (values: SeasonParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.BLOCK_SEASON, values, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getSeason(values));
        return response.data;
      } else {
        toastr.error(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const getSeasonDetails = createAsyncThunk(
  'admin/getSeasonDetails',
  async (obj: { seasonId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.SEASON_DETAIL, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { seasonDetails: response.data.data };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== VERSION ACTIONS ====================
export const getVersion = createAsyncThunk(
  'admin/getVersion',
  async (params: VersionParams, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.VERSION_LIST, params, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { versionData: response.data.data, versionCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const addVersion = createAsyncThunk(
  'admin/addVersion',
  async (authObj: VersionParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.ADD_VERSION, authObj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getVersion({ page: 0 }));
        return response.data;
      } else if ([201, 409].includes(response.data.code)) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const removeVersion = createAsyncThunk(
  'admin/removeVersion',
  async (obj: { versionId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.REMOVE_VERSION, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getVersion({ page: 0 }));
        return response.data;
      } else if (response.data.code === 201) {
        toastr.warning(response.data.message);
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== DASHBOARD ACTIONS ====================
export const getAdminCounts = createAsyncThunk(
  'admin/getAdminCounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_URL + ENDPOINTS.ADMIN_COUNT_LIST, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { dashboardCounts: response.data.data };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== LADDER ACTIONS ====================
export const getLadder = createAsyncThunk(
  'admin/getLadder',
  async (params: LadderParams, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_URL + ENDPOINTS.USER_LADDER, {
        params,
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return { ladderData: response.data.data, ladderDataCount: response.data.totalCount };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== AUTO TIPPING ====================
export const autoTipping = createAsyncThunk(
  'admin/autoTipping',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_URL + ENDPOINTS.AUTO_TIPPING, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        return response.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== SEND GAME START NOTIFICATION ====================
export const sendGameStartNotification = createAsyncThunk(
  'admin/sendGameStartNotification',
  async (obj: { gameId: string; selectedSeason: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.SEND_GAME_START_NOTIFICATION, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        return response.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== UPDATE GAME SEASON ====================
export const updateGameSeason = createAsyncThunk(
  'admin/updateGameSeason',
  async (obj: { selectedSeason: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + ENDPOINTS.UPDATE_GAME_SEASON, obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getGame({ page: 0, season: obj.selectedSeason }));
        return response.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== BANTER ACTIONS ====================
export const getCompbySport = createAsyncThunk(
  'admin/getCompbySport',
  async (obj: { sportId: string; count: number }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL + 'competition/getCompListbySportId', obj, {
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const GetCsvData = createAsyncThunk(
  'admin/GetCsvData',
  async (obj: { compId: string; startDate: Date; endDate: Date }, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_URL + 'chat/getdownload', {
        params: obj,
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return response.data.message;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// ==================== ARTICLE ACTIONS ====================
export const getArticleList = createAsyncThunk(
  'admin/getArticleList',
  async (params: ArticleParams = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_URL + ENDPOINTS.ARTICLE_LIST, {
        params,
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        return {
          articleData: response.data.data?.docs || [],
          articleCount: response.data.data?.totalDocs || 0,
          articleLimit: response.data.data?.limit || 10,
          currentPage: response.data.data?.page || 1,
        };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

export const updateArticleStatus = createAsyncThunk(
  'admin/updateArticleStatus',
  async (obj: { _id: string; isActive?: boolean; isDeleted?: boolean }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.delete(API_URL + ENDPOINTS.ARTICLE_ACTIVE_DELETE, {
        params: obj,
        headers: getAuthHeaders(),
      });
      if (response.data.code === 200) {
        toastr.success(response.data.message);
        dispatch(getArticleList({}));
        return response.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
    }
  }
);

// Create the slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Sport
      .addCase(getSport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSport.fulfilled, (state, action) => {
        state.loading = false;
        state.sportData = action.payload.sportData;
        state.sportCount = action.payload.sportCount;
      })
      .addCase(getSport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Team
      .addCase(getTeam.fulfilled, (state, action) => {
        state.teamData = action.payload.teamData;
        state.teamCount = action.payload.teamCount;
      })
      .addCase(getTeamDetails.fulfilled, (state, action) => {
        state.teamDetails = action.payload.teamDetails;
      })
      // Bonus
      .addCase(getBonus.fulfilled, (state, action) => {
        state.bonusData = action.payload.bonusData;
        state.bonusCount = action.payload.bonusCount;
      })
      // Round
      .addCase(getRound.fulfilled, (state, action) => {
        state.roundData = action.payload.roundData;
        state.roundCount = action.payload.roundCount;
      })
      // Game
      .addCase(getGame.fulfilled, (state, action) => {
        state.gameData = action.payload.gameData;
        state.gameCount = action.payload.gameCount;
      })
      // Topic
      .addCase(getTopic.fulfilled, (state, action) => {
        state.topicData = action.payload.topicData;
        state.topicCount = action.payload.topicCount;
      })
      // Question
      .addCase(getQuestion.fulfilled, (state, action) => {
        state.questionData = action.payload.questionData;
        state.questionCount = action.payload.questionCount;
      })
      // Content
      .addCase(getContent.fulfilled, (state, action) => {
        state.contentData = action.payload.contentData;
        state.contentCount = action.payload.contentCount;
      })
      // Users
      .addCase(getUsers.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.userCount = action.payload.userCount;
      })
      // Competitions
      .addCase(getCompetitions.fulfilled, (state, action) => {
        state.competitionData = action.payload.competitionData;
        state.competitionCount = action.payload.competitionCount;
      })
      // Comp
      .addCase(getComp.fulfilled, (state, action) => {
        state.compData = action.payload.compData;
        state.compCount = action.payload.compCount;
      })
      // Settings
      .addCase(getSetting.fulfilled, (state, action) => {
        state.settingData = action.payload.settingData;
      })
      // Ad
      .addCase(getAd.fulfilled, (state, action) => {
        state.adData = action.payload.adData;
        state.adCount = action.payload.adCount;
      })
      .addCase(getAdReports.fulfilled, (state, action) => {
        state.adreportData = action.payload.adreportData;
        state.adreportCount = action.payload.adreportCount;
      })
      // Season
      .addCase(getSeason.fulfilled, (state, action) => {
        state.seasonData = action.payload.seasonData;
        state.seasonCount = action.payload.seasonCount;
      })
      .addCase(getSeasonDetails.fulfilled, (state, action) => {
        state.seasonDetails = action.payload.seasonDetails;
      })
      // Version
      .addCase(getVersion.fulfilled, (state, action) => {
        state.versionData = action.payload.versionData;
        state.versionCount = action.payload.versionCount;
      })
      // Dashboard
      .addCase(getAdminCounts.fulfilled, (state, action) => {
        state.dashboardCounts = action.payload.dashboardCounts;
      })
      // Ladder
      .addCase(getLadder.fulfilled, (state, action) => {
        state.ladderData = action.payload.ladderData;
        state.ladderDataCount = action.payload.ladderDataCount;
      })
      // Article
      .addCase(getArticleList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getArticleList.fulfilled, (state, action) => {
        state.loading = false;
        state.articleData = action.payload.articleData;
        state.articleCount = action.payload.articleCount;
        state.articleLimit = action.payload.articleLimit;
      })
      .addCase(getArticleList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = adminSlice.actions;
export default adminSlice.reducer;
